import { useEffect } from 'react';
import dayjs from 'dayjs';
import { meName } from '../CustomerDemo/softphone';
import {
  AFTERCARE_TAG, COMPLAINT_TAG, MANAGER, MEDICAL_TAG, SURVEY_TAG, SYSTEM_AGENT, callCenterApi, getCallCenter, servicePrice,
  type Appointment, type Callback, type CallLog, type ContactChannel, type Customer, type State, type Survey,
} from '../CustomerDemo/callCenterStore';

/**
 * Dữ liệu demo CSKH sát thực tế (front-end): cuộc gọi 7 ngày của nhiều nhân viên, lần làm dịch vụ,
 * khảo sát, khiếu nại, việc gọi lại / hỏi thăm / bất thường. Mọi id có tiền tố DEMO_PREFIX để gỡ ra được.
 */
export const DEMO_PREFIX = 'demo-';
const isDemo = (x: { id: string }) => x.id.startsWith(DEMO_PREFIX);

export const hasCskhDemo = (s: State) => s.callbacks.some(isDemo) || s.calls.some(isDemo);

export function removeCskhDemo(s: State): State {
  return {
    ...s,
    calls: s.calls.filter(x => !isDemo(x)),
    callbacks: s.callbacks.filter(x => !isDemo(x)),
    surveys: s.surveys.filter(x => !isDemo(x)),
    appointments: s.appointments.filter(x => !isDemo(x)),
  };
}

const AGENTS = ['tp03', 'Nguyễn Như Huệ', 'Trực page 02', 'Telesale 1', 'Telesale 2'];
const PRACTITIONERS = ['BS. Lan Anh', 'BS. Quốc Bảo', 'KTV Thu Trang'];
const CALL_NOTES = [
  'Hỏi thăm sau dịch vụ, khách phản hồi vùng điều trị ổn định.',
  'Nhắc lịch tái khám, khách xác nhận đến đúng hẹn.',
  'Khách hỏi chương trình ưu đãi tháng này, đã gửi báo giá qua Zalo.',
  'Tư vấn liệu trình tiếp theo, khách hẹn cuối tuần phản hồi.',
  'Khách hỏi cách chăm sóc tại nhà, đã hướng dẫn chi tiết.',
  'Xác nhận lịch hẹn và gửi địa chỉ chi nhánh.',
];
const H = 3600000;
const D = 86400000;

/** me: người đang đăng nhập — giao vài việc để chế độ "Của tôi" có dữ liệu */
export function withCskhDemo(input: State, now = Date.now(), me?: string): State {
  const s = removeCskhDemo(input);
  const cs: Customer[] = s.customers.filter(c => c.name && c.phones[0]);
  if (cs.length < 6) return s;
  const pick = (i: number) => cs[i % cs.length];
  const phone = (c: Customer) => c.phones[0];
  const start = dayjs(now).startOf('day').valueOf();
  let n = 0;
  const id = (k: string) => `${DEMO_PREFIX}${k}-${n++}`;

  // ---------- Cuộc gọi 7 ngày ----------
  const calls: CallLog[] = [];
  for (let d = 0; d < 7; d++) {
    const count = d === 0 ? 18 : 9 + (d % 3) * 2;
    for (let k = 0; k < count; k++) {
      const c = pick(d * 5 + k * 3);
      const agent = AGENTS[(k + d) % AGENTS.length];
      const incoming = k % 3 === 0;
      const outcome = k % 6 === 4 ? 'no_answer' : k % 9 === 7 ? 'busy' : 'answered';
      const at = d === 0
        ? Math.max(start + 7.5 * H + k * 60000, now - (k * 19 + 6) * 60000)
        : start - d * D + 8 * H + k * 37 * 60000;
      if (at > now) continue;
      const answered = outcome === 'answered';
      calls.push({
        id: id('call'), phone: phone(c), direction: incoming ? 'in' : 'out', outcome, startedAt: at,
        duration: answered ? 45 + ((k * 53 + d * 17) % 320) : 0,
        agent: !answered && incoming ? SYSTEM_AGENT : agent,
        note: answered ? CALL_NOTES[(k + d) % CALL_NOTES.length] : undefined,
        // Cuộc nhỡ cũ đã được xử lý; giữ 2 cuộc nhỡ hôm nay để có việc gọi lại
        ...(!answered && incoming && (d > 0 || k > 6) ? { handled: true } : {}),
      });
    }
  }

  // ---------- Lần làm dịch vụ đã xong (1–19 ngày trước) ----------
  const appts: Appointment[] = Array.from({ length: 10 }, (_, i) => {
    const c = pick(i + 1);
    const service = c.service || 'Cấy tóc';
    const at = dayjs(start - (i * 2 + 1) * D).hour(9 + (i % 4) * 2).valueOf();
    return {
      id: id('ap'), customerId: c.id, at, service, branch: c.branch || 'Quận 2', status: 'done', by: c.agent,
      createdAt: at - 5 * D, price: servicePrice(service) || 6500000, practitioner: PRACTITIONERS[i % PRACTITIONERS.length],
      log: [{ at: at + 3 * H, by: c.agent, text: 'Đã xác nhận → Đã thực hiện' }],
    };
  });
  const custOf = (a: Appointment) => cs.find(c => c.id === a.customerId)!;

  // ---------- Lịch hẹn trong 24 giờ tới (cần gọi nhắc / xác nhận) ----------
  const upcoming: Appointment[] = [2, 5, 9, 20].map((hours, i) => {
    const c = pick(i + 12);
    const service = c.service || ['Cấy tóc', 'Làm đẹp da', 'Nâng mũi'][i % 3];
    // Giữ trong giờ làm việc 8h–19h: rơi ngoài giờ thì dời sang sáng hôm sau
    let t = dayjs(now + hours * H).minute(i % 2 ? 30 : 0).second(0);
    if (t.hour() >= 19 || t.hour() < 8) t = t.add(t.hour() >= 19 ? 1 : 0, 'day').hour(8 + i).minute(30);
    if (t.valueOf() > now + 24 * H) t = t.subtract(1, 'day');
    if (t.valueOf() <= now) t = dayjs(now + (i + 1) * H).minute(0).second(0);
    const at = t.valueOf();
    return {
      id: id('ap'), customerId: c.id, at, service, branch: c.branch || 'Quận 2', status: i % 2 ? 'confirmed' : 'new', by: c.agent,
      createdAt: now - (i + 2) * D, price: servicePrice(service) || 6500000, practitioner: PRACTITIONERS[(i + 1) % PRACTITIONERS.length],
      note: ['Khách mới, lần đầu đến', 'Tái khám sau 1 tháng', 'Khách đi cùng người nhà', 'Buổi 2 liệu trình'][i],
    };
  });

  // ---------- Khảo sát ----------
  const channels: ContactChannel[] = ['call', 'zalo', 'call', 'counter'];
  const sv = (i: number, over: Partial<Survey>): Survey => ({
    id: id('sv'), customerId: appts[i].customerId, appointmentId: appts[i].id, service: 5, attitude: 5, practitionerScore: 5,
    by: AGENTS[i % AGENTS.length], at: appts[i].at + 3 * D + 2 * H, status: 'done', channel: channels[i % channels.length], ...over,
  });
  const surveys: Survey[] = [
    sv(2, { service: 5, attitude: 5, practitionerScore: 5, nps: 10, comment: 'Bác sĩ tư vấn kỹ, rất hài lòng.' }),
    sv(3, { service: 4, attitude: 5, practitionerScore: 4, nps: 9, comment: 'Kết quả tốt, phòng chờ hơi đông.' }),
    sv(4, { service: 2, attitude: 3, practitionerScore: 2, nps: 4, comment: 'Vùng điều trị vẫn còn đỏ, chưa thấy cải thiện.' }),
    sv(5, { service: 5, attitude: 4, practitionerScore: 5, nps: 8 }),
    sv(6, { service: 0, attitude: 0, practitionerScore: undefined, status: 'declined', comment: 'Khách bận, không muốn khảo sát' }),
    sv(7, { service: 0, attitude: 0, practitionerScore: undefined, status: 'unreachable', at: now - 2 * D, comment: 'Thuê bao không liên lạc được' }),
    sv(7, { service: 0, attitude: 0, practitionerScore: undefined, status: 'unreachable', at: now - D, comment: 'Không nghe máy' }),
    sv(8, { service: 3, attitude: 1, practitionerScore: 4, nps: 2, comment: 'Chờ 45 phút mới được làm, lễ tân trả lời cộc lốc.' }),
    sv(9, { service: 4, attitude: 4, practitionerScore: 5, nps: 9, comment: 'Sẽ giới thiệu bạn bè.' }),
  ];
  const lowA = surveys[2];
  const lowB = surveys[7];

  // ---------- Việc / khiếu nại ----------
  const cb = (c: Customer, over: Partial<Callback>): Callback => ({
    id: id('cb'), phone: phone(c), dueAt: now, by: c.agent || AGENTS[0], createdAt: now - 2 * H, ...over,
  });
  const ap = (i: number) => appts[i];
  const reopenedSource = cb(pick(5), {
    note: `${COMPLAINT_TAG} Tóc mọc không đều sau 1 tháng`, createdAt: now - 6 * D, dueAt: now - 6 * D + 2 * H,
    complaint: { category: 'result', severity: 'high', level: 1 }, doneAt: now - 4 * D, doneBy: 'Telesale 1',
    result: 'Đã hẹn khách tái khám đánh giá lại', satisfied: false,
  });
  const callbacks: Callback[] = [
    // Khiếu nại
    cb(custOf(ap(4)), {
      note: `${COMPLAINT_TAG} CSAT thấp (DV 2★ · TĐ 3★ · ${ap(4).practitioner} 2★) — ${lowA.comment}`, appointmentId: ap(4).id,
      createdAt: now - 5 * H, dueAt: now - 3 * H, complaint: { category: 'result', severity: 'medium', appointmentId: ap(4).id, surveyId: lowA.id, level: 1 },
    }),
    cb(custOf(ap(8)), {
      note: `${COMPLAINT_TAG} CSAT thấp (DV 3★ · TĐ 1★) — ${lowB.comment}`, appointmentId: ap(8).id, createdAt: lowB.at, dueAt: lowB.at + H,
      complaint: { category: 'attitude', severity: 'high', appointmentId: ap(8).id, surveyId: lowB.id, level: 1 },
      doneAt: lowB.at + 50 * 60000, doneBy: 'Nguyễn Như Huệ', result: 'Đã gọi xin lỗi, tặng voucher chăm sóc da miễn phí', satisfied: true,
    }),
    cb(pick(2), { note: `${COMPLAINT_TAG} Khách thắc mắc bị tính phí tái khám`, createdAt: now - 30 * 60000, dueAt: now + 90 * 60000, complaint: { category: 'price', severity: 'medium', level: 1 } }),
    cb(pick(3), {
      note: `${COMPLAINT_TAG} Đến đúng hẹn nhưng phải chờ lâu`, createdAt: now - 6 * H, dueAt: now + H, assignee: MANAGER,
      complaint: { category: 'waiting', severity: 'low', level: 2 }, escalated: { by: 'tp03', at: now - 2 * H },
    }),
    reopenedSource,
    cb(pick(5), {
      note: `${COMPLAINT_TAG} Mở lại — khách chưa hài lòng: Đã hẹn khách tái khám đánh giá lại`, createdAt: now - 4 * D, dueAt: now - H, assignee: MANAGER,
      complaint: { category: 'result', severity: 'high', level: 2, reopenedFrom: reopenedSource.id }, escalated: { by: 'Telesale 1', at: now - 4 * D },
    }),
    // Bất thường sau dịch vụ
    cb(custOf(ap(0)), { note: `${MEDICAL_TAG} Sưng, chảy dịch nhẹ vùng điều trị — bác sĩ gọi lại hướng dẫn`, appointmentId: ap(0).id, assignee: ap(0).practitioner, createdAt: now - H, dueAt: now - 40 * 60000 }),
    // Hỏi thăm sau dịch vụ
    cb(custOf(ap(0)), { note: `${AFTERCARE_TAG} Hỏi thăm ngày 1 sau ${ap(0).service}`, appointmentId: ap(0).id, createdAt: ap(0).at, dueAt: dayjs(now).hour(15).minute(0).valueOf() }),
    cb(custOf(ap(1)), { note: `${AFTERCARE_TAG} Hỏi thăm ngày 3 sau ${ap(1).service}`, appointmentId: ap(1).id, createdAt: ap(1).at, dueAt: start - 6 * H }),
    // Khảo sát
    cb(custOf(ap(1)), { note: `${SURVEY_TAG} Khảo sát hài lòng (${ap(1).service})`, appointmentId: ap(1).id, createdAt: ap(1).at, dueAt: dayjs(now).hour(10).minute(0).valueOf() }),
    cb(custOf(ap(7)), {
      note: `${SURVEY_TAG} Khảo sát hài lòng (${ap(7).service})`, appointmentId: ap(7).id, createdAt: ap(7).at, dueAt: now - 2 * H,
      reschedules: [
        { from: now - 2 * D - 2 * H, to: now - D - 2 * H, reason: 'Không liên lạc được (lần 1/3)', by: 'Telesale 2', at: now - 2 * D },
        { from: now - D - 2 * H, to: now - 2 * H, reason: 'Không liên lạc được (lần 2/3)', by: 'Telesale 2', at: now - D },
      ],
    }),
    // Gọi lại thường
    cb(pick(6), { note: 'Khách hẹn gọi lại sau giờ làm để chốt lịch', createdAt: now - D, dueAt: now - 5 * H }),
    cb(pick(7), {
      note: 'Gửi lại báo giá liệu trình 5 buổi', createdAt: now - 4 * D, dueAt: now - 2 * D,
      reschedules: [{ from: now - 3 * D, to: now - 2 * D, reason: 'Khách bận, hẹn giờ khác', by: 'Telesale 2', at: now - 3 * D }],
    }),
    cb(pick(8), { note: 'Xác nhận lịch tái khám tuần sau', createdAt: now - 3 * H, dueAt: dayjs(now).hour(17).minute(0).valueOf() }),
    cb(pick(9), { note: 'Tư vấn thêm gói chăm sóc da sau điều trị', createdAt: now - H, dueAt: now + 2 * D }),
    cb(pick(10), { note: 'Nhắc khách mang theo đơn thuốc khi tái khám', createdAt: now - H, dueAt: now + 4 * D }),
    // Việc của người đang đăng nhập
    ...(me ? [
      cb(pick(13), { note: 'Gọi lại tư vấn gói chăm sóc da 3 buổi', createdAt: now - D, dueAt: now - 90 * 60000, assignee: me }),
      cb(pick(14), { note: `${AFTERCARE_TAG} Hỏi thăm ngày 7 sau Nâng mũi`, createdAt: now - 7 * D, dueAt: dayjs(now).hour(16).minute(30).valueOf(), assignee: me }),
      cb(pick(15), { note: 'Xác nhận khách đã nhận hoá đơn điện tử', createdAt: now - 2 * D, dueAt: now - D, assignee: me, doneAt: now - 2 * H, doneBy: me, result: 'Khách đã nhận qua email' }),
    ] : []),
    // Đã xong trong tuần
    ...[0, 1, 2, 3, 4, 5].map(i => cb(pick(11 + i), {
      note: ['Gọi lại tư vấn lịch', 'Nhắc lịch hẹn', 'Gửi hướng dẫn chăm sóc', 'Xác nhận thanh toán', 'Gửi báo giá', 'Hỏi thăm khách cũ'][i],
      createdAt: now - (i + 2) * D, dueAt: now - (i + 1) * D, doneAt: now - (i + 1) * D + (i % 2 ? 2 * H : -H), doneBy: AGENTS[i % AGENTS.length],
      result: ['Khách đồng ý đặt lịch', 'Khách xác nhận đến', 'Đã gửi qua Zalo', 'Khách đã chuyển khoản', 'Khách hẹn cân nhắc', 'Khách hài lòng'][i],
    })),
  ];

  return {
    ...s,
    calls: [...s.calls, ...calls],
    appointments: [...s.appointments, ...appts, ...upcoming],
    surveys: [...surveys, ...s.surveys],
    callbacks: [...s.callbacks, ...callbacks],
  };
}

// ---------- Tự nạp dữ liệu demo ----------

/** Người dùng đã chủ động gỡ demo thì không tự nạp lại */
const DEMO_OFF_KEY = 'khothi.cskh.demo.off';
const demoOff = () => { try { return localStorage.getItem(DEMO_OFF_KEY) === '1'; } catch { return false; } };
const setDemoOff = (off: boolean) => { try { if (off) localStorage.setItem(DEMO_OFF_KEY, '1'); else localStorage.removeItem(DEMO_OFF_KEY); } catch { /* bỏ qua */ } };

export function loadCskhDemo() {
  setDemoOff(false);
  callCenterApi.reset(withCskhDemo(getCallCenter(), Date.now(), meName()));
}

export function clearCskhDemo() {
  setDemoOff(true);
  callCenterApi.reset(removeCskhDemo(getCallCenter()));
}

/** Dữ liệu demo nạp từ hôm trước → mốc "hôm nay / quá hạn" đã lệch, cần làm mới */
export function isDemoStale(s: State, now = Date.now()) {
  const last = Math.max(0, ...s.calls.filter(isDemo).map(c => c.startedAt));
  return last < dayjs(now).startOf('day').valueOf();
}

/** Mở trang CSKH ở trình duyệt chưa có (hoặc có bản cũ) dữ liệu demo → tự nạp, trừ khi người dùng đã gỡ */
export function useCskhDemoAutoload() {
  useEffect(() => {
    const s = getCallCenter();
    if (!demoOff() && (!hasCskhDemo(s) || isDemoStale(s))) callCenterApi.reset(withCskhDemo(s, Date.now(), meName()));
  }, []);
}
